// src/components/TermsAndCondition.jsx
import React, { useMemo } from "react";

import NoticeBox from "./common/NoticeBox";

const TermsAndCondition = () => {
  const header = useMemo(
    () => ({
      badge: "Legal Information",
      title: "Terms & Conditions",
      description:
        "Please read these terms and conditions carefully before using our service. By accessing or using our website, you agree to be bound by these terms.",
      lastUpdated: "Last Updated: February 11, 2026",
    }),
    []
  );

  const termsData = useMemo(
    () => [
      {
        title: "1. Acceptance of Terms",
        content: `By accessing and using this website, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.

These Terms and Conditions govern your use of our website and services. By creating an account or making a purchase, you acknowledge that you have read, understood, and agree to be bound by these terms.

We reserve the right to modify these terms at any time. Any changes will be effective immediately upon posting on this page. Your continued use of our services after such modifications constitutes your acceptance of the updated terms.`,
      },
      {
        title: "2. Use License",
        content: `Permission is granted to temporarily download one copy of the materials information or software on our website for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title.

Under this license you may not:

• Modify or copy the materials
• Use the materials for any commercial purpose
• Attempt to decompile or reverse engineer any software contained on our website
• Remove any copyright or proprietary notations from the materials
• Transfer the materials to another person or mirror the materials on another server

This license shall automatically terminate if you violate any of these restrictions and may be terminated by us at any time.`,
      },
      {
        title: "3. Product Information and Pricing",
        content: `We strive to provide accurate product descriptions, images, and pricing information. However, we do not warrant that product descriptions, pricing, or other content on this website is accurate, complete, reliable, current, or error-free.

Prices for our products are subject to change without notice. We reserve the right to modify or discontinue any product without prior notice.

In the event of a pricing error, we reserve the right to cancel any orders placed at the incorrect price.

All prices are listed in United States Dollars USD and are subject to applicable sales tax. Shipping costs are calculated at checkout and vary based on your location and chosen shipping method.`,
      },
      {
        title: "4. Account Registration",
        content: `To make a purchase or access certain features, you may need to create an account.

You are responsible for:

• Providing accurate, current, and complete information during registration
• Maintaining the security of your account password
• All activities that occur under your account
• Notifying us immediately of any unauthorized use of your account

You must be at least 18 years old or the age of majority in your jurisdiction to create an account.

We reserve the right to suspend or terminate accounts that violate these terms or engage in fraudulent activity.`,
      },
      {
        title: "5. Orders and Payment",
        content: `When you place an order, you are making an offer to purchase products at the listed price. We reserve the right to accept or decline your order for any reason, including product availability, pricing errors, product information errors, or suspected fraudulent activity.

Payment is required at the time of purchase. We accept major credit cards, debit cards, and other payment methods as indicated at checkout.

By providing payment information, you confirm that:

• You are legally authorized to use the payment method
• The information you provide is true and accurate
• Charges incurred will be honored by your payment provider

In the event of payment failure or dispute, we may cancel your order.`,
      },
      {
        title: "6. Shipping and Delivery",
        content: `We ship to addresses within the United States and select international locations. Shipping costs and estimated delivery times are provided at checkout.

Delivery timeframes are estimates and not guaranteed. We are not responsible for delays caused by carriers, customs, weather conditions, or other circumstances beyond our control.

Risk of loss and title for products pass to you upon delivery to the shipping carrier.

You are responsible for providing accurate shipping information. We are not liable for orders shipped to incorrect addresses provided by you.

International orders may be subject to customs duties, taxes, and fees imposed by the destination country.`,
      },
      {
        title: "7. Returns and Refunds",
        content: `We want you to be satisfied with your purchase. If you are not completely satisfied, you may return most items within 30 days of delivery for a refund or exchange.

Return conditions:

• Items must be unused, in original condition, and in original packaging
• Certain items may be non-returnable
• Return shipping costs are the responsibility of the customer unless the return is due to our error
• Refunds are processed to the original payment method within 7 to 10 business days of receiving the returned item

To initiate a return, please contact our customer service team.

Damaged or defective items should be reported within 48 hours of delivery.`,
      },
      {
        title: "8. Intellectual Property Rights",
        content: `All content on this website, including text, graphics, logos, images, audio clips, digital downloads, data compilations, and software, is the property of our company or our content suppliers.

The content is protected by United States and international copyright laws.

You may not reproduce, distribute, modify, transmit, or use any content from this website for commercial purposes without our written permission.

Our trademarks, service marks, and logos used on this website are our registered and unregistered trademarks.`,
      },
      {
        title: "9. Privacy and Data Protection",
        content: `Your privacy is important to us. Our Privacy Policy explains how we collect, use, and protect your personal information.

By using our website, you consent to our collection and use of personal information as outlined in our Privacy Policy.

We implement appropriate security measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction.

We do not sell your personal information to third parties. We may share information with service providers who assist us in operating our website and conducting business under strict confidentiality agreements.`,
      },
      {
        title: "10. Limitation of Liability",
        content: `To the maximum extent permitted by law, our company and its officers, directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages.

This includes losses resulting from:

• Your use or inability to use our services
• Unauthorized access to or use of our servers
• Interruption or cessation of transmission to or from our website
• Bugs, viruses, or similar harmful elements transmitted by third parties
• Errors or omissions in any content

Our total liability to you for all claims arising from or related to our services shall not exceed the amount you paid to us in the twelve months preceding the claim.`,
      },
      {
        title: "11. Prohibited Activities",
        content: `You agree not to use our website or services for any unlawful purpose or in any way that violates these Terms and Conditions.

Prohibited activities include:

• Violating any applicable law
• Attempting to gain unauthorized access to our systems
• Uploading viruses or malicious code
• Using scraping, bots, or automated data collection without permission
• Harassing, abusing, or harming other users
• Impersonating another person or entity
• Using our services for unauthorized commercial purposes
• Interfering with the proper functioning of our website
• Attempting to bypass security features

We reserve the right to terminate your access immediately if you engage in prohibited activities.`,
      },
      {
        title: "12. Dispute Resolution and Governing Law",
        content: `These Terms and Conditions and any dispute arising out of your use of our website shall be governed by the laws of the United States and the state in which our company is incorporated.

Any disputes shall be resolved through binding arbitration in accordance with the American Arbitration Association's Commercial Arbitration Rules.

You agree to waive your right to a jury trial and to participate in class action lawsuits.

If any provision of these terms is found to be unenforceable, the remaining provisions will continue in full force and effect.`,
      },
      {
        title: "13. Indemnification",
        content: `You agree to defend, indemnify, and hold harmless our company, its affiliates, licensors, service providers, officers, directors, employees, contractors, agents, suppliers, successors, and assigns.

This applies to claims, liabilities, damages, judgments, losses, costs, expenses, or fees arising from:

• Your violation of these Terms and Conditions
• Your use of our website or services
• Your violation of any third-party rights
• Your violation of any applicable law or regulation
• Any content you post or submit to our website

This obligation will survive termination of these Terms and Conditions.`,
      },
      {
        title: "14. Third-Party Links and Services",
        content: `Our website may contain links to third-party websites, advertisers, or services that are not owned or controlled by us.

We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party websites or services.

You acknowledge that we are not responsible or liable for any damage or loss caused by your use of third-party websites or services.

We recommend reading the terms and privacy policies of any third-party websites you visit.`,
      },
      {
        title: "15. Termination",
        content: `We may terminate or suspend your access to our services immediately, without prior notice or liability, for any reason, including breach of these Terms and Conditions.

Upon termination, your right to use our services will immediately cease.

If you wish to terminate your account, you may discontinue using our services or contact us to request account deletion.

All provisions that should survive termination shall survive, including ownership provisions, warranty disclaimers, indemnity, and limitations of liability.`,
      },
      {
        title: "16. Contact Information",
        content: `If you have any questions about these Terms and Conditions, please contact us:

Email: admin@ifjona.com
Phone: +1 347-854-7073
117-05 197th St, Saint Albans, NY 11412
United States

Our customer service team is available Monday through Friday, 9:00 AM to 6:00 PM EST.

For legal inquiries, please contact legal@yourcompany.com.

Last Updated: February 11, 2026`,
      },
    ],
    []
  );

  const notice = useMemo(
    () => ({
      type: "warning",
      icon: "ph-warning",
      title: "Legal Disclaimer",
      message:
        "These terms and conditions constitute a legally binding agreement between you and our company. If you do not agree with any part of these terms, you must not use our website or services.",
    }),
    []
  );

  return (
    <section className="terms-conditions">
      <style>
        {`
          .terms-conditions {
            padding: 40px 0;
            background: #fafafa;
          }

          .terms-conditions .terms-wrapper {
            margin: 0 auto;
          }

          .terms-conditions .terms-header {
            text-align: center;
            margin-bottom: 30px;
          }

          .terms-conditions .terms-badge {
            display: inline-block;
            font-size: 13px;
            font-weight: 600;
            color: #0d6efd;
            background: #eef5ff;
            padding: 6px 14px;
            border-radius: 50px;
            margin-bottom: 12px;
          }

          .terms-conditions .terms-title {
            font-size: 34px;
            line-height: 1.2;
            margin-bottom: 10px;
            color: #222;
          }

          .terms-conditions .terms-description {
            max-width: 620px;
            margin: 0 auto 10px;
            font-size: 15px;
            line-height: 1.6;
            color: #666;
          }

          .terms-conditions .last-updated {
            font-size: 13px;
            color: #888;
          }

          .terms-conditions .terms-section {
            background: #fff;
            border: 1px solid #eee;
            border-radius: 12px;
            padding: 22px;
            margin-bottom: 16px;
          }

          .terms-conditions .terms-section-title {
            font-size: 20px;
            line-height: 1.3;
            margin-bottom: 12px;
            color: #222;
            font-weight: 700;
          }

          .terms-conditions .terms-section-text {
            font-size: 14px;
            line-height: 1.7;
            color: #666;
            margin-bottom: 0;
            white-space: pre-line;
          }

          .terms-conditions .notice-small {
            margin-top: 24px;
          }

          @media (max-width: 575px) {
            .terms-conditions {
              padding: 30px 0;
            }

            .terms-conditions .terms-title {
              font-size: 28px;
            }

            .terms-conditions .terms-section {
              padding: 18px;
            }

            .terms-conditions .terms-section-title {
              font-size: 18px;
            }
          }
        `}
      </style>

      <div className="container">
        <div className="terms-wrapper">
          <div className="terms-header">
            <span className="terms-badge">{header.badge}</span>

            <h1 className="terms-title">{header.title}</h1>

            <p className="terms-description">{header.description}</p>

            <div className="last-updated">{header.lastUpdated}</div>
          </div>

          {termsData.map((section, index) => (
            <div key={index} className="terms-section">
              <h3 className="terms-section-title">{section.title}</h3>
              <p className="terms-section-text">{section.content}</p>
            </div>
          ))}

          <NoticeBox
            type={notice.type}
            icon={notice.icon}
            title={notice.title}
            message={notice.message}
            className="notice-small"
          />
        </div>
      </div>
    </section>
  );
};

export default TermsAndCondition;